import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import withAuth from "../../utils/withAuth";
import { fetchCallAllDataByIDAPI } from "../../utils/api";
import ContentWrapper from "../../components/contentwrapper/ContentWrapper";
import ReportNavBar from "../../components/ReportNavBar";

const SKELETON_COUNT = 10;

const SkeletonTableCard = () => (
    <div className="col-md-4 col-lg-3 col-sm-6 mb-4">
        <div className="card h-100 shadow-sm">
            <div className="card-body p-2">
                <div className="skeleton" style={{ height: 18, width: "70%", marginBottom: 10 }} />
                <div className="skeleton" style={{ height: 14, width: "100%", marginBottom: 6 }} />
                <div className="skeleton" style={{ height: 14, width: "100%", marginBottom: 6 }} />
                <div className="skeleton" style={{ height: 14, width: "80%", marginBottom: 6 }} />
            </div>
        </div>
    </div>
);

const perCallCost = (spend, calls) => {
    const c = Number(calls || 0);
    const s = Number(spend || 0);
    if (c <= 0) return 0;
    return s / c;
};

const YesterdayTeamCall = () => {
    const [process, setProcess] = useState([]);   // rows
    const [summary, setSummary] = useState(null); // summary
    const [isLoading, setIsLoading] = useState(false);

    // ✅ Date range (default = yesterday)
    const yesterdayStr = () => {
        const d = new Date();
        d.setDate(d.getDate() - 1);
        return d.toISOString().slice(0, 10);
    };
    const [fromDate, setFromDate] = useState(() => yesterdayStr());
    const [toDate, setToDate] = useState(() => yesterdayStr());
    const [appliedDate, setAppliedDate] = useState({ from: "", to: "" });

    // ✅ Skill search (applied)
    const [skillQuery, setSkillQuery] = useState("");
    const [appliedSkillQuery, setAppliedSkillQuery] = useState("");

    // ✅ Team multi-select dropdown
    const [teamOpen, setTeamOpen] = useState(false);
    const [teamSearch, setTeamSearch] = useState("");
    const [selectedTeams, setSelectedTeams] = useState(new Set()); // working
    const [appliedTeams, setAppliedTeams] = useState(new Set());   // applied

    const navigate = useNavigate();
    const ppcid = localStorage.getItem("ppcdata");

    // ✅ TEAM MAP (supports multiple letters like L, Q etc)
    const TEAM_BY_CODE = {
        K: "Vivek Kumar",
        R: "Pragya Singh",
        J: "Akash Deep",
        V: "Pradeep Verma",
        T: "Yanshi",
        N: "Deepika Sisodia",
        D: "Deepak Kumar Giri",
        S: "Sumit Saini",
        I: "Ranjan Kumar",
        B: "Navin Chaurasia",
        E: "Bhumika Gola",
        G: "Vimal Jadon",
        P: "Anurodh Baral",
        M: "Simrati Kaur",
        Z: "Manprit Sir",
        A: "Ankita",
        W: "Ishika Pokhriyal",
        U: "Om Kumar",
        L: "Navin Chaurasia",
        Q: "Akash Deep",
    };

    // ✅ IMPORTANT: Only take PREFIX CODE for team
    // Example: "K_HIN_LIV..." => team = K
    const deriveTeamName = (skillRaw) => {
        const skill = String(skillRaw || "").trim();
        if (!skill) return "Other";

        const parts = skill.split("_").filter(Boolean);
        const first = (parts[0] || "").toUpperCase(); // PREFIX

        // Only show proper team names in dropdown
        if (TEAM_BY_CODE[first]) return TEAM_BY_CODE[first];

        // anything not mapped -> Other (so you won't see random "AM", "D2C" etc)
        return "Other";
    };

    const fmt = (n) => Number(n || 0).toLocaleString("en-IN");
    const fmtMoney = (n) =>
        Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const getLiveCallWithProcessName = async (id, d1, d2) => {
        setIsLoading(true);
        try {
            const qs = new URLSearchParams();
            if (d1) qs.set("start_date", d1);
            if (d2) qs.set("end_date", d2);

            const res = await fetchCallAllDataByIDAPI(
                `ppc-team-get-yesterday-call-with-proccess-name-by-id/${id}?${qs.toString()}`
            );

            if (res?.data?.status === 1) {
                const p = res?.data?.process || {};
                setProcess(Array.isArray(p?.rows) ? p.rows : []);
                setSummary(p?.summary || null);
            } else {
                toast.error(res?.data?.message || "Failed to load process");
                setProcess([]);
                setSummary(null);
            }
        } catch (e) {
            console.error(e);
            toast.error("Network/CORS error while loading process");
            setProcess([]);
            setSummary(null);
        } finally {
            setIsLoading(false);
        }
    };

    // ✅ Default load (yesterday)
    useEffect(() => {
        if (!ppcid) return navigate("/");
        navigate("/yesterday-team-call-report");

        const d = yesterdayStr();
        setAppliedDate({ from: d, to: d });
        getLiveCallWithProcessName(ppcid, d, d);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ppcid]);

    // ✅ Group raw data by TEAM
    const groupedDataRaw = useMemo(() => {
        const groups = {};
        (process || []).forEach((row) => {
            const code = String(row?.skillname || "");
            const calls = Number(row?.unique_calls || 0);
            const orders = Number(row?.order_count || 0);
            const spend = Number(row?.spend_yesterday || 0);

            const teamName = deriveTeamName(code);

            if (!groups[teamName]) {
                groups[teamName] = { name: teamName, totalCalls: 0, totalOrders: 0, totalSpend: 0, items: [] };
            }

            groups[teamName].totalCalls += calls;
            groups[teamName].totalOrders += orders;
            groups[teamName].totalSpend += spend;

            groups[teamName].items.push({
                code,
                calls,
                orders,
                spend,
                perCall: perCallCost(spend, calls),
                perf: calls ? (orders / calls) * 100 : 0,
            });
        });

        Object.values(groups).forEach((g) => {
            g.perf = g.totalCalls ? (g.totalOrders / g.totalCalls) * 100 : 0;
            g.perCall = perCallCost(g.totalSpend, g.totalCalls);

            g.items.sort((a, b) => (b.calls !== a.calls ? b.calls - a.calls : a.code.localeCompare(b.code)));
        });

        return Object.values(groups).sort((a, b) =>
            b.totalCalls !== a.totalCalls ? b.totalCalls - a.totalCalls : a.name.localeCompare(b.name)
        );
    }, [process]);


    // ✅ Team dropdown options (remove Other if you want)
    const teamOptions = useMemo(() => {
        const opts = Array.from(new Set(groupedDataRaw.map((g) => g.name).filter(Boolean)));
        // If you don't want "Other" in dropdown, uncomment next line:
        // return opts.filter(x => x !== "Other").sort((a,b)=>a.localeCompare(b));
        return opts.sort((a, b) => a.localeCompare(b));
    }, [groupedDataRaw]);

    const filteredTeamOptions = useMemo(() => {
        const q = String(teamSearch || "").trim().toLowerCase();
        if (!q) return teamOptions;
        return teamOptions.filter((t) => t.toLowerCase().includes(q));
    }, [teamOptions, teamSearch]);

    // ✅ initialize teams to ALL if empty
    useEffect(() => {
        if (!teamOptions.length) return;
        setSelectedTeams((prev) => (prev && prev.size ? prev : new Set(teamOptions)));
        setAppliedTeams((prev) => (prev && prev.size ? prev : new Set(teamOptions)));
    }, [teamOptions]);

    const toggleTeam = (name) => {
        setSelectedTeams((prev) => {
            const next = new Set(prev);
            if (next.has(name)) next.delete(name);
            else next.add(name);
            return next;
        });
    };

    const onSelectAllTeams = () => setSelectedTeams(new Set(teamOptions));
    const onClearTeams = () => setSelectedTeams(new Set());
    const onApplyTeams = () => {
        if (!selectedTeams.size) {
            const all = new Set(teamOptions);
            setSelectedTeams(all);
            setAppliedTeams(all);
            toast.info("No team selected, showing All Teams");
            setTeamOpen(false);
            return;
        }
        setAppliedTeams(new Set(selectedTeams));
        setTeamOpen(false);
    };
    const onResetTeams = () => {
        const all = new Set(teamOptions);
        setSelectedTeams(all);
        setAppliedTeams(all);
        setTeamSearch("");
        setTeamOpen(false);
    };

    const teamButtonText = useMemo(() => {
        if (!appliedTeams || appliedTeams.size === 0) return "All Teams";
        if (appliedTeams.size === teamOptions.length) return "All Teams";
        if (appliedTeams.size === 1) return Array.from(appliedTeams)[0];
        return `${appliedTeams.size} Teams`;
    }, [appliedTeams, teamOptions]);

    // ✅ apply Team + Skill filter on grouped data
    const groupedData = useMemo(() => {
        const q = String(appliedSkillQuery || "").trim().toLowerCase();
        const teams = appliedTeams && appliedTeams.size ? appliedTeams : new Set(teamOptions);

        let list = groupedDataRaw.filter((g) => teams.has(g.name));

        if (q) {
            list = list
                .map((g) => {
                    const items = (g.items || []).filter((it) => String(it.code || "").toLowerCase().includes(q));
                    if (!items.length) return null;

                    const totalCalls = items.reduce((s, it) => s + (Number(it.calls) || 0), 0);
                    const totalOrders = items.reduce((s, it) => s + (Number(it.orders) || 0), 0);
                    const totalSpend = items.reduce((s, it) => s + (Number(it.spend) || 0), 0);

                    return {
                        ...g,
                        items,
                        totalCalls,
                        totalOrders,
                        totalSpend,
                        perf: totalCalls ? (totalOrders / totalCalls) * 100 : 0,
                    };
                })
                .filter(Boolean);
        }

        return list;
    }, [groupedDataRaw, appliedTeams, teamOptions, appliedSkillQuery]);

    // ✅ Summary top bar
    const totalCalls = useMemo(() => {
        if (summary && typeof summary.total_unique_calls !== "undefined") return Number(summary.total_unique_calls) || 0;
        return (process || []).reduce((s, r) => s + (Number(r?.unique_calls) || 0), 0);
    }, [process, summary]);

    const totalOrders = useMemo(() => {
        if (summary && typeof summary.total_orders_created !== "undefined") return Number(summary.total_orders_created) || 0;
        return (process || []).reduce((s, r) => s + (Number(r?.order_count) || 0), 0);
    }, [process, summary]);

    const totalSpend = useMemo(() => {
        if (summary && typeof summary.total_spend_yesterday !== "undefined") return Number(summary.total_spend_yesterday) || 0;
        return (process || []).reduce((s, r) => s + (Number(r?.spend_yesterday) || 0), 0);
    }, [process, summary]);

    const perf = useMemo(() => (totalCalls ? (totalOrders / totalCalls) * 100 : 0), [totalCalls, totalOrders]);
    const perCall = useMemo(() => perCallCost(totalSpend, totalCalls), [totalSpend, totalCalls]);

    // ✅ Apply filters
    const onApplyFilters = () => {
        const d1 = String(fromDate || "");
        const d2 = String(toDate || "");
        if (!d1 || !d2) return toast.error("Please select From Date and To Date");
        if (d1 > d2) return toast.error("From Date cannot be greater than To Date");

        setAppliedDate({ from: d1, to: d2 });
        setAppliedSkillQuery(skillQuery || "");
        setAppliedTeams(new Set(selectedTeams.size ? selectedTeams : teamOptions));
        setTeamOpen(false);

        if (ppcid) getLiveCallWithProcessName(ppcid, d1, d2);
    };

    const onResetFilters = () => {
        const d = yesterdayStr();
        setFromDate(d);
        setToDate(d);
        setSkillQuery("");
        setAppliedSkillQuery("");

        const all = new Set(teamOptions);
        setSelectedTeams(all);
        setAppliedTeams(all);

        setAppliedDate({ from: d, to: d });
        setTeamSearch("");
        setTeamOpen(false);

        if (ppcid) getLiveCallWithProcessName(ppcid, d, d);
    };

    const headerTitle = useMemo(() => {
        const d1 = appliedDate.from || fromDate;
        const d2 = appliedDate.to || toDate;
        if (d1 && d2) return `Yesterday Team (${d1} → ${d2})`;
        return "Yesterday Team";
    }, [appliedDate, fromDate, toDate]);

    return (
        <div className="misDashboard">
            <Helmet>
                <title>SATKARTAR::DASHBOARD</title>
            </Helmet>

            <style>{`
        .skeleton{position:relative;overflow:hidden;background:#eee;border-radius:6px}
        .skeleton:after{content:"";position:absolute;inset:0;transform:translateX(-100%);
          background:linear-gradient(90deg,transparent,rgba(255,255,255,.6),transparent);
          animation:shimmer 1.2s infinite}
        @keyframes shimmer{100%{transform:translateX(100%)}}

        .mini-table{ table-layout: fixed; width:100%; }
        .mini-table th,.mini-table td{font-size:12px; padding:6px 8px; vertical-align:middle;}

        .skillCell{
          white-space:normal !important;
          word-break:break-word;
          overflow-wrap:anywhere;
          line-height:1.15;
        }

        .stickyHead { position: sticky; top: 0; z-index: 2; background: #fff; }
        .skillStickyHeader { background:#fff; }
        .colHead th{ background:#fafafa; }

        .filterRow{display:flex;gap:8px;align-items:end;flex-wrap:wrap;}
        .filterBox{display:flex;flex-direction:column;gap:4px;}
        .filterBox label{font-weight:600;font-size:12px;margin:0;}

        /* ✅ Team checkbox dropdown */
        .teamDropWrap{position:relative; min-width:260px; }
        .teamDropBtn{
          width:260px;
          display:flex; align-items:center; justify-content:space-between;
          padding:6px 10px;
          border:1px solid rgba(0,0,0,.15);
          border-radius:6px;
          background:#fff;
          font-size:13px;
        }
        .teamDropMenu{
          position:absolute; top:100%; left:0; z-index:50;
          width:260px;
          background:#fff;
          border:1px solid rgba(0,0,0,.15);
          border-radius:10px;
          margin-top:6px;
          box-shadow:0 10px 30px rgba(0,0,0,.12);
          overflow:hidden;
        }
        .teamDropHead{padding:10px; border-bottom:1px solid rgba(0,0,0,.08);}
        .teamSearch{
          width:100%;
          border:1px solid rgba(0,0,0,.15);
          border-radius:8px;
          padding:7px 10px;
          font-size:13px;
          outline:none;
        }
        .teamActions{
          display:flex; padding:10px;
          border-bottom:1px solid rgba(0,0,0,.08);
          justify-content:space-between;
        }
        .teamList{max-height:240px; overflow:auto; padding:8px 5px;}
        .teamItem{display:flex; align-items:center; gap:10px; padding:6px 2px; font-size:13px;}
        .teamFoot{
          display:flex; gap:8px; justify-content:space-between;
          padding:10px;
          border-top:1px solid rgba(0,0,0,.08);
          background:#fafafa;
        }
      `}</style>

            <ContentWrapper>
                <div className="row">
                    <div className="col-12">
                        <ReportNavBar />
                    </div>
                </div>

                {/* ✅ Top Summary */}
                <div className="">
                    <h5 className="livecallcount">
                        {headerTitle}
                        {"  |  "}
                        Calls: {isLoading ? "..." : fmt(totalCalls)}
                        {"  |  "}
                        Orders: {isLoading ? "..." : fmt(totalOrders)}
                        {"  |  "}
                        Spend: {isLoading ? "..." : `₹${fmtMoney(totalSpend)}`}
                        {"  |  "}
                        Perf: {isLoading ? "..." : `${perf.toFixed(1)}%`}
                        {"  |  "}
                        Per Call:{" "}
                        <span style={{ color: perCall > 100 ? "#d32f2f" : "#111", fontWeight: 700 }}>
                            {isLoading ? "..." : `₹${fmtMoney(perCall)}`}
                        </span>
                    </h5>
                </div>

                {/* ✅ Filters */}
                <div className="card shadow-sm mt-2">
                    <div className="card-body p-2">
                        <div className="filterRow">
                            <div className="filterBox">
                                <label>From Date</label>
                                <input
                                    type="date"
                                    className="form-control form-control-sm"
                                    value={fromDate}
                                    onChange={(e) => setFromDate(e.target.value)}
                                    disabled={isLoading}
                                />
                            </div>

                            <div className="filterBox">
                                <label>To Date</label>
                                <input
                                    type="date"
                                    className="form-control form-control-sm"
                                    value={toDate}
                                    onChange={(e) => setToDate(e.target.value)}
                                    disabled={isLoading}
                                />
                            </div>

                            {/* ✅ Team dropdown */}
                            <div className="filterBox">
                                <label>Team Name</label>
                                <div className="teamDropWrap">
                                    <button
                                        type="button"
                                        className="teamDropBtn"
                                        onClick={() => setTeamOpen((s) => !s)}
                                        disabled={isLoading || !teamOptions.length}
                                    >
                                        <span style={{ fontWeight: 600 }}>{teamButtonText}</span>
                                        <span style={{ opacity: 0.7 }}>▾</span>
                                    </button>

                                    {teamOpen && (
                                        <div className="teamDropMenu">
                                            <div className="teamDropHead">
                                                <input
                                                    className="teamSearch"
                                                    value={teamSearch}
                                                    onChange={(e) => setTeamSearch(e.target.value)}
                                                    placeholder="Search team..."
                                                />
                                            </div>

                                            <div className="teamActions">
                                                <button type="button" className="btnselectall" onClick={onSelectAllTeams}>
                                                    Select All
                                                </button>
                                                <button type="button" className="btnresetall" onClick={onClearTeams}>
                                                    Clear
                                                </button>
                                            </div>

                                            <div className="teamList">
                                                {filteredTeamOptions.length ? (
                                                    filteredTeamOptions.map((name) => (
                                                        <label key={name} className="teamItem">
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedTeams.has(name)}
                                                                onChange={() => toggleTeam(name)}
                                                            />
                                                            <span>{name}</span>
                                                        </label>
                                                    ))
                                                ) : (
                                                    <div className="text-muted" style={{ fontSize: 13, padding: "8px 2px" }}>
                                                        No teams found
                                                    </div>
                                                )}
                                            </div>

                                            <div className="teamFoot">
                                                <button type="button" className="btnselectall" onClick={onResetTeams}>
                                                    Reset All
                                                </button>
                                                <button type="button" className="btnresetall" onClick={onApplyTeams}>
                                                    Apply Teams
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* ✅ Skill search */}
                            <div className="filterBox">
                                <label>Search Skill</label>
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="Search skill..."
                                    value={skillQuery}
                                    onChange={(e) => setSkillQuery(e.target.value)}
                                    disabled={isLoading}
                                />
                            </div>

                            <button className="btnskillapply" onClick={onApplyFilters} disabled={isLoading}>
                                Apply
                            </button>

                            <button className="btnresetall" onClick={onResetFilters} disabled={isLoading}>
                                Reset
                            </button>
                        </div>
                    </div>
                </div>

                {/* ✅ Cards */}
                <div className="row mt-3 livecallbody">
                    {isLoading
                        ? Array.from({ length: SKELETON_COUNT }).map((_, i) => <SkeletonTableCard key={`sk-${i}`} />)
                        : groupedData.map((group, idx) => (
                            <div className="col-md-6 col-lg-4 mb-3" key={idx}>
                                <div className="card shadow-sm">
                                    <div className="card-body p-2">
                                        <div className="p-0" style={{ maxHeight: 260, overflowY: "auto" }}>
                                            <table className="table table-sm table-bordered mb-0 mini-table">
                                                <thead className="stickyHead">
                                                    <tr>
                                                        <th colSpan={6} className="skillStickyHeader">
                                                            <div className="d-flex justify-content-between">
                                                                <div className="sh-left">{group.name}</div>
                                                                <div className="sh-right">
                                                                    <span>Calls: <b>{fmt(group.totalCalls)}</b></span>{" "}
                                                                    <span>| Orders: <b>{fmt(group.totalOrders)}</b></span>{" "}
                                                                    <span>| ₹{fmtMoney(group.totalSpend)}</span>{" "}
                                                                    <span>
                                                                        | Cost:{" "}
                                                                        <b style={{ color: group.perCall > 100 ? "#d32f2f" : "#111" }}>
                                                                            ₹{fmtMoney(group.perCall)}
                                                                        </b>
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </th>
                                                    </tr>

                                                    <tr className="colHead">
                                                        <th>Skill</th>
                                                        <th style={{ width: 30, textAlign: "right" }}>Calls</th>
                                                        <th style={{ width: 30, textAlign: "right" }}>Orders</th>
                                                        <th style={{ width: 60, textAlign: "right" }}>Spend</th>
                                                        <th style={{ width: 60, textAlign: "right" }}>Cost</th>
                                                        <th style={{ width: 45, textAlign: "right" }}>Perf</th>
                                                    </tr>
                                                </thead>

                                                <tbody>
                                                    {group.items.map((item, ix) => (
                                                        <tr key={ix}>
                                                            <td className="skillCell">{item.code}</td>
                                                            <td style={{ textAlign: "right" }}>{fmt(item.calls)}</td>
                                                            <td style={{ textAlign: "right" }}>{fmt(item.orders)}</td>
                                                            <td style={{ textAlign: "right" }}>₹{fmtMoney(item.spend)}</td>
                                                            <td style={{ textAlign: "right", color: item.perCall > 100 ? "#d32f2f" : "#111" }}>
                                                                ₹{fmtMoney(item.perCall)}
                                                            </td>
                                                            <td
                                                                style={{
                                                                    textAlign: "right",
                                                                    fontWeight: 800,
                                                                    display: "flex",
                                                                    justifyContent: "flex-end",
                                                                    alignItems: "center",
                                                                    gap: 4,
                                                                    color:
                                                                        item.calls === 0
                                                                            ? "#999"
                                                                            : (item.orders / item.calls) >= 0.15
                                                                                ? "#2e7d32"
                                                                                : "#d32f2f",
                                                                }}
                                                            >
                                                                {item.calls === 0 ? (
                                                                    <>
                                                                        <span style={{ fontSize: 12 }}>—</span>
                                                                        <span>0.0%</span>
                                                                    </>
                                                                ) : (item.orders / item.calls) >= 0.15 ? (
                                                                    <>
                                                                        <span style={{ fontSize: 12 }}>↑</span>
                                                                        <span>{((item.orders / item.calls) * 100).toFixed(1)}%</span>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <span style={{ fontSize: 12 }}>↓</span>
                                                                        <span>{((item.orders / item.calls) * 100).toFixed(1)}%</span>
                                                                    </>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}

                    {!isLoading && groupedData.length === 0 && (
                        <div className="col-12">
                            <div className="alert alert-info mb-0">No data for selected filters.</div>
                        </div>
                    )}
                </div>
            </ContentWrapper>
        </div>
    );
};

export default withAuth(YesterdayTeamCall);
